import { useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { useFormStore } from "../store/formStore";
import {
  WHAT_YOULL_NEED,
  WHAT_HAPPENS_AFTER_SUBMIT_INTRO,
  WHAT_HAPPENS_AFTER_SUBMIT,
  IMPORTANT_NOTES,
} from "../content/welcomeContent";

interface InfoDrawerProps {
  open: boolean;
  onClose: () => void;
}

export function InfoDrawer({ open, onClose }: InfoDrawerProps) {
  const bodyRef = useRef<HTMLDivElement>(null);
  const uwMethod = useFormStore((s) => s.meta.uwMethod);
  const whatYoullNeed = useMemo(
    () => WHAT_YOULL_NEED.filter((item) => !(item.hideForClaims && uwMethod === "Claims")),
    [uwMethod],
  );

  useEffect(() => {
    if (open && bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [open]);

  // Close on Escape key
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  // Lock body scroll while open — position:fixed is required for iOS Safari,
  // which ignores overflow:hidden on the body.
  useEffect(() => {
    if (!open) return;
    const y = window.scrollY;
    document.body.style.position = "fixed";
    document.body.style.top = `-${y}px`;
    document.body.style.width = "100%";
    return () => {
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      window.scrollTo(0, y);
    };
  }, [open]);

  return createPortal(
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black/40 z-[60] transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <aside
        className={`fixed top-0 right-0 h-full w-full max-w-md bg-white z-[60] shadow-2xl flex flex-col transition-transform duration-300 ease-in-out ${open ? "translate-x-0" : "translate-x-full"}`}
        aria-label="Instructions & Info"
        aria-hidden={!open}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
          <h2 className="text-base font-bold text-brand-navy">Instructions &amp; Info</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 text-2xl leading-none"
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        {/* Body */}
        <div ref={bodyRef} className="overflow-y-auto overscroll-y-contain flex-1 px-6 py-5 space-y-6 text-[15px]">
          <div className="rounded-lg p-4 bg-brand-navy">
            <h3 className="text-white font-bold text-base m-0">Thank You for Choosing Optimyl Benefits</h3>
          </div>

          <section className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h4 className="font-bold text-brand-navy mb-3 pb-2 border-b-2 border-brand-green">
              What You'll Need to Complete This Form
            </h4>
            <ul className="list-none p-0 m-0">
              {whatYoullNeed.map((item, i, arr) => (
                <li key={i} className={`flex gap-2 py-2 text-gray-700 leading-relaxed${i < arr.length - 1 ? " border-b border-gray-200" : ""}`}>
                  <span className="mt-2 w-1.5 h-1.5 rounded-full shrink-0 bg-brand-green" />
                  <span>{item.content}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h4 className="font-bold text-brand-navy mb-3 pb-2 border-b-2 border-brand-green">
              What Happens After You Submit
            </h4>
            <p className="text-gray-500 leading-relaxed mb-3">{WHAT_HAPPENS_AFTER_SUBMIT_INTRO}</p>
            <ol className="list-none p-0 m-0">
              {WHAT_HAPPENS_AFTER_SUBMIT.map((item, i, arr) => (
                <li key={i} className={`flex gap-3 py-2 text-gray-700 leading-relaxed${i < arr.length - 1 ? " border-b border-gray-200" : ""}`}>
                  <span className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold mt-0.5 bg-brand-navy">
                    {i + 1}
                  </span>
                  <div><strong>{item.label}</strong> {item.detail}</div>
                </li>
              ))}
            </ol>
          </section>

          <section className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h4 className="font-bold text-brand-navy mb-3 pb-2 border-b-2 border-brand-green">
              A Few Important Notes
            </h4>
            <ul className="list-none p-0 m-0">
              {IMPORTANT_NOTES.map((item, i, arr) => (
                <li key={i} className={`flex gap-2 py-2 text-gray-700 leading-relaxed${i < arr.length - 1 ? " border-b border-gray-200" : ""}`}>
                  <span className="mt-2 w-1.5 h-1.5 rounded-full shrink-0 bg-brand-green" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>
        </div>
      </aside>
    </>,
    document.body,
  );
}
